// src/pages/GameLevel1.jsx
// ─── Doron's Lullaby ── Twinkle Twinkle, Little Star ──────────────────────────
// 3D crystal ball bouncer game integrated with LevelTemplate
// Black notes = 1 beat, Pink notes = 2 beats (wider tile, no bounce needed)
// ────────────────────────────────────────────────────────────────────────────────

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import { TrophyStorage } from "../services/trophyStorage";

/* ─── BPM / TIMING ────────────────────────────────────────────────────────── */
const BPM = 72;
const BEAT_MS = (60 / BPM) * 1000; // ≈833 ms per beat
/* ─── CANVAS DIMENSIONS ────────────────────────────────────────────────────── */
const CW = 480;
const CH = 300;
const HORIZON_Y = 55;
const BALL_Y = 228; // hit zone Y
const MAX_HALF_W = 196;
const VISIBLE_BEATS = 10;
const BALL_R = 16;

/* ─── STATIC STARS (generated once) ────────────────────────────────────────── */
const STARS = Array.from({ length: 80 }, (_, i) => ({
  x: (Math.sin(i * 137.508) * 0.5 + 0.5),
  y: (Math.cos(i * 97.201) * 0.5 + 0.5) * 0.85,
  r: 0.4 + (i % 5) * 0.3,
  a: 0.3 + (i % 7) * 0.1,
}));

/* ─── NOTE COLORS ───────────────────────────────────────────────────────────── */
const NOTE_COLORS = {
  C: { fill: "#ef4444", dark: "#991b1b", highlight: "#fca5a5", text: "#fff" },
  D: { fill: "#f97316", dark: "#9a3412", highlight: "#fdba74", text: "#fff" },
  E: { fill: "#eab308", dark: "#854d0e", highlight: "#fde047", text: "#1a1a1a" },
  F: { fill: "#22c55e", dark: "#14532d", highlight: "#86efac", text: "#fff" },
  G: { fill: "#06b6d4", dark: "#164e63", highlight: "#67e8f9", text: "#fff" },
  A: { fill: "#8b5cf6", dark: "#4c1d95", highlight: "#c4b5fd", text: "#fff" },
  B: { fill: "#ec4899", dark: "#831843", highlight: "#f9a8d4", text: "#fff" },
};

/* ─── TWINKLE TWINKLE — note data with beat durations ───────────────────────
   Black = 1 beat  |  Pink (2) = 2 beats  (per the sheet music PDF)
   Schema:
     Part 1:  C C G G A A [G²]  F F E E D D [C²]
     Part 2:  G G F F E E [D²]  G G F F E E [D²]
     Part 3:  C C G G A A [G²]  F F E E D D [C²]
   ──────────────────────────────────────────────────────────────────────────── */
const GAME_NOTES = [
  // ── Part 1a: Twinkle Twinkle Little Star ──
  { note: "C", beats: 1 }, { note: "C", beats: 1 },
  { note: "G", beats: 1 }, { note: "G", beats: 1 },
  { note: "A", beats: 1 }, { note: "A", beats: 1 },
  { note: "G", beats: 2 },                           // pink = 2 beats
  // ── Part 1b: How I Wonder What You Are ──
  { note: "F", beats: 1 }, { note: "F", beats: 1 },
  { note: "E", beats: 1 }, { note: "E", beats: 1 },
  { note: "D", beats: 1 }, { note: "D", beats: 1 },
  { note: "C", beats: 2 },                           // pink
  // ── Part 2a: Up Above The World So High ──
  { note: "G", beats: 1 }, { note: "G", beats: 1 },
  { note: "F", beats: 1 }, { note: "F", beats: 1 },
  { note: "E", beats: 1 }, { note: "E", beats: 1 },
  { note: "D", beats: 2 },                           // pink
  // ── Part 2b: Like A Diamond In The Sky ──
  { note: "G", beats: 1 }, { note: "G", beats: 1 },
  { note: "F", beats: 1 }, { note: "F", beats: 1 },
  { note: "E", beats: 1 }, { note: "E", beats: 1 },
  { note: "D", beats: 2 },                           // pink
  // ── Part 3a: Twinkle Twinkle Little Star ──
  { note: "C", beats: 1 }, { note: "C", beats: 1 },
  { note: "G", beats: 1 }, { note: "G", beats: 1 },
  { note: "A", beats: 1 }, { note: "A", beats: 1 },
  { note: "G", beats: 2 },                           // pink
  // ── Part 3b: How I Wonder What You Are ──
  { note: "F", beats: 1 }, { note: "F", beats: 1 },
  { note: "E", beats: 1 }, { note: "E", beats: 1 },
  { note: "D", beats: 1 }, { note: "D", beats: 1 },
  { note: "C", beats: 2 },                           // pink  ← final note
];

/* Build tile objects with cumulative beat positions */
function buildTiles() {
  let pos = 2; // start 2 beats ahead so first tile approaches naturally
  return GAME_NOTES.map((n, i) => {
    const t = { idx: i, note: n.note, beats: n.beats, beatStart: pos };
    pos += n.beats;
    return t;
  });
}
const TILES = buildTiles();
const TOTAL_TILES = TILES.length;
/* ─── MELODY LINES for LevelTemplate progress tracking ─────────────────────── */
const MELODY_LINES = [
  {
    id: 0,
    notes: GAME_NOTES.slice(0, 14).map((n) => n.note),
    lyric: "Twinkle, twinkle, little star...",
  },
  {
    id: 1,
    notes: GAME_NOTES.slice(14, 28).map((n) => n.note),
    lyric: "Up above the world so high...",
  },
  {
    id: 2,
    notes: GAME_NOTES.slice(28, 42).map((n) => n.note),
    lyric: "Twinkle, twinkle, little star!",
  },
];

const LYRIC_GROUPS = [
  ["Twinkle Twinkle", "Little Star", "How I Wonder", "What You Are"],
  ["Up Above", "The World So", "High Like A", "Diamond Sky"],
  ["Twinkle Twinkle", "Little Star", "How I Wonder", "What You Are!"],
];

const NOTE_GROUP_INDEXES = MELODY_LINES.map(() => [
  { start: 0, end: 1 },
  { start: 2, end: 5 },
  { start: 6, end: 9 },
  { start: 10, end: 13 },
]);

/* ─── CANVAS HELPERS ─────────────────────────────────────────────────────────── */

/** Map z-distance in beats to {y, halfW} screen coords */
function project(zBeats) {
  const t = Math.max(0, Math.min(1, zBeats / VISIBLE_BEATS));
  return {
    y: BALL_Y - (BALL_Y - HORIZON_Y) * t,
    hw: MAX_HALF_W * (1 - t),
  };
}

function drawBackground(ctx) {
  // Sky gradient
  const sky = ctx.createLinearGradient(0, 0, 0, CH);
  sky.addColorStop(0, "#04021a");
  sky.addColorStop(0.5, "#0f0736");
  sky.addColorStop(1, "#1e0f52");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, CW, CH);

  // Stars
  STARS.forEach((s) => {
    ctx.globalAlpha = s.a;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(s.x * CW, s.y * (HORIZON_Y + 10), s.r, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.globalAlpha = 1;

  // Horizon glow
  const hg = ctx.createRadialGradient(CW / 2, HORIZON_Y, 0, CW / 2, HORIZON_Y, CW * 0.55);
  hg.addColorStop(0, "rgba(139,92,246,0.35)");
  hg.addColorStop(1, "rgba(139,92,246,0)");
  ctx.fillStyle = hg;
  ctx.fillRect(0, 0, CW, CH);

  // Track underside (dark fill below tiles)
  ctx.beginPath();
  ctx.moveTo(CW / 2, HORIZON_Y);
  ctx.lineTo(0, CH);
  ctx.lineTo(CW, CH);
  ctx.closePath();
  ctx.fillStyle = "rgba(8,4,30,0.7)";
  ctx.fill();
}

function drawTile(ctx, tile, currentBeat, showLabel) {
  const col = NOTE_COLORS[tile.note] || NOTE_COLORS.C;
  const zFront = tile.beatStart - currentBeat;
  const zBack = tile.beatStart + tile.beats - currentBeat;

  if (zBack < -0.2 || zFront > VISIBLE_BEATS + 0.5) return;

  const front = project(Math.max(0, zFront));
  const back = project(Math.max(0.05, zBack));
  const cx = CW / 2;

  // Tile body
  ctx.beginPath();
  ctx.moveTo(cx - back.hw, back.y);
  ctx.lineTo(cx + back.hw, back.y);
  ctx.lineTo(cx + front.hw, front.y);
  ctx.lineTo(cx - front.hw, front.y);
  ctx.closePath();

  // Gradient fill (lighter at top/back, darker at front)
  const tg = ctx.createLinearGradient(0, back.y, 0, front.y);
  tg.addColorStop(0, col.dark + "bb");
  tg.addColorStop(1, col.fill);
  ctx.fillStyle = tg;
  ctx.fill();

  // Front edge highlight
  ctx.beginPath();
  ctx.moveTo(cx - front.hw, front.y);
  ctx.lineTo(cx + front.hw, front.y);
  ctx.strokeStyle = col.highlight;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Side edges
  ctx.beginPath();
  ctx.moveTo(cx - back.hw, back.y);
  ctx.lineTo(cx - front.hw, front.y);
  ctx.moveTo(cx + back.hw, back.y);
  ctx.lineTo(cx + front.hw, front.y);
  ctx.strokeStyle = col.dark + "88";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Note label (training wheels)
  if (showLabel && zFront > 0.1 && zFront < VISIBLE_BEATS - 0.5) {
    const midZ = (Math.max(0.1, zFront) + zBack) / 2;
    const mid = project(midZ);
    const fs = Math.max(10, mid.hw * 0.35);
    ctx.save();
    ctx.font = `900 ${fs}px 'Arial Rounded MT Bold', Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // Shadow for readability
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillText(tile.note, cx + 1, mid.y - 3 + 1);
    ctx.fillStyle = col.text;
    ctx.fillText(tile.note, cx + 1, mid.y - 3);
    ctx.restore();
  }

  // "Long tile" label when beats > 1
  if (tile.beats > 1 && zFront > 0 && zFront < VISIBLE_BEATS) {
    const midZ = (Math.max(0.1, zFront) + zBack) / 2;
    const mid = project(midZ);
    const fs = Math.max(8, mid.hw * 0.18);
    ctx.save();
    ctx.font = `bold ${fs}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillText("HOLD", cx - 1, mid.y + fs * 1.4);
    ctx.restore();
  }
}

function drawBall(ctx, ballX, ballY, glowIntensity, fallAlpha) {
  ctx.save();
  ctx.globalAlpha = fallAlpha;

  // Drop shadow
  const shadowScale = Math.max(0.1, 1 - (ballY - BALL_Y) * 0.003);
  ctx.beginPath();
  ctx.ellipse(ballX, BALL_Y + 8, BALL_R * 1.1 * shadowScale, BALL_R * 0.35 * shadowScale, 0, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,0,0,0.4)";
  ctx.fill();

  // Glow
  const glow = ctx.createRadialGradient(ballX, ballY, BALL_R * 0.5, ballX, ballY, BALL_R * 2.5);
  glow.addColorStop(0, `rgba(147,197,253,${0.3 * glowIntensity})`);
  glow.addColorStop(1, "rgba(147,197,253,0)");
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(ballX, ballY, BALL_R * 2.5, 0, Math.PI * 2);
  ctx.fill();

  // Ball body
  const body = ctx.createRadialGradient(ballX - 5, ballY - 5, 1, ballX, ballY, BALL_R);
  body.addColorStop(0, "#ffffff");
  body.addColorStop(0.2, "#e0f2fe");
  body.addColorStop(0.65, "#7dd3fc");
  body.addColorStop(1, "#1d4ed8");
  ctx.beginPath();
  ctx.arc(ballX, ballY, BALL_R, 0, Math.PI * 2);
  ctx.fillStyle = body;
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.7)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Highlight
  ctx.beginPath();
  ctx.arc(ballX - 5, ballY - 5, 5, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.fill();

  ctx.restore();
}

function drawHitZoneLine(ctx) {
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, BALL_Y);
  ctx.lineTo(CW, BALL_Y);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

function drawSparkles(ctx, x, y, count) {
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const dist = BALL_R * 1.8 + Math.sin(Date.now() * 0.005 + i) * 4;
    const sx = x + Math.cos(angle) * dist;
    const sy = y + Math.sin(angle) * dist;
    ctx.beginPath();
    ctx.arc(sx, sy, 2, 0, Math.PI * 2);
    ctx.fillStyle = "#fde047";
    ctx.globalAlpha = 0.6 + Math.sin(Date.now() * 0.008 + i) * 0.3;
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/* ─── BALL BOUNCER GAME COMPONENT ───────────────────────────────────────────── */
function BallBouncerGame({
  matchedCount = 0,
  noteEvent,
  isComplete = false,
  hasStarted = false,
  totalNotes,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  // ─── Persistent game state (in ref for perf) ─────────────────────────────
  const gs = useRef({
    currentBeat: 0,
    startTime: null,
    matchedCount: 0,
    // Ball animation
    ballAnim: "idle",     // 'idle' | 'bounce' | 'fall' | 'recover'
    ballY: BALL_Y,
    ballVY: 0,            // velocity for fall/recover
    animStartTime: 0,
    bounceHeight: 38,
    // Miss flash
    missFlash: 0,
    // Hit sparkles
    hitSparkles: 0,
    // Doron state
    doronState: "awake",  // 'awake' | 'groggy' | 'sleepy' | 'asleep' | 'fire'
    fireTimer: 0,
  });

  // ─── React state (for UI re-renders) ─────────────────────────────────────
  const [showGuide, setShowGuide] = useState(true);
  const [doronEmotion, setDoronEmotion] = useState("awake");

  const prevMatchedCount = useRef(0);
  const prevNoteEvent = useRef(null);

  // ─── Start clock when game begins ─────────────────────────────────────────
  useEffect(() => {
    if (hasStarted) {
      gs.current.startTime = performance.now();
      gs.current.currentBeat = 0;
    }
  }, [hasStarted]);

  // ─── Handle correct note (matchedCount increased) ─────────────────────────
  useEffect(() => {
    if (matchedCount > prevMatchedCount.current) {
      prevMatchedCount.current = matchedCount;
      const g = gs.current;
      g.matchedCount = matchedCount;
      g.ballAnim = "bounce";
      g.ballY = BALL_Y;
      g.animStartTime = performance.now();
      g.hitSparkles = 8;

      // Sleepiness increases with progress
      const pct = matchedCount / TOTAL_TILES;
      if (pct >= 1) {
        g.doronState = "asleep";
        setDoronEmotion("asleep");
      } else if (pct >= 0.75) {
        g.doronState = "sleepy";
        setDoronEmotion("sleepy");
      } else if (pct >= 0.4) {
        g.doronState = "groggy";
        setDoronEmotion("groggy");
      }
    }
  }, [matchedCount]);

  // ─── Handle miss event ───────────────────────────────────────────────────
  useEffect(() => {
    if (noteEvent === "miss" && noteEvent !== prevNoteEvent.current) {
      prevNoteEvent.current = noteEvent;
      const g = gs.current;
      g.missFlash = 1.0;
      g.fireTimer = 1800; // ms of fire animation
      g.ballAnim = "fall";
      g.ballY = BALL_Y;
      g.ballVY = 0;
      g.animStartTime = performance.now();
      if (g.doronState !== "asleep") {
        g.doronState = "fire";
        setDoronEmotion("fire");
      }
    }
    if (noteEvent !== "miss") prevNoteEvent.current = noteEvent;
  }, [noteEvent]);

  // ─── isComplete ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isComplete) {
      gs.current.doronState = "asleep";
      setDoronEmotion("asleep");
    }
  }, [isComplete]);

  // ─── Main canvas draw loop ─────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    function frame(now) {
      rafRef.current = requestAnimationFrame(frame);
      const g = gs.current;

      // Advance clock
      if (hasStarted && g.startTime) {
        g.currentBeat = (now - g.startTime) / BEAT_MS;
      }

      // Tick fire timer
      if (g.fireTimer > 0) {
        g.fireTimer -= 16; // approx 1 frame at 60fps
        if (g.fireTimer <= 0) {
          g.fireTimer = 0;
          if (g.doronState === "fire") {
            const pct = g.matchedCount / TOTAL_TILES;
            if (pct >= 0.75) g.doronState = "sleepy";
            else if (pct >= 0.4) g.doronState = "groggy";
            else g.doronState = "awake";
            setDoronEmotion(g.doronState);
          }
        }
      }

      // Miss flash decay
      if (g.missFlash > 0) g.missFlash = Math.max(0, g.missFlash - 0.04);

      // Hit sparkles decay
      if (g.hitSparkles > 0) g.hitSparkles = Math.max(0, g.hitSparkles - 0.15);

      // Ball animation
      let ballDrawY = BALL_Y;
      let fallAlpha = 1;
      let glowIntensity = 1;

      if (g.ballAnim === "bounce") {
        const elapsed = now - g.animStartTime;
        const progress = Math.min(1, elapsed / (BEAT_MS * 0.7));
        ballDrawY = BALL_Y - g.bounceHeight * Math.sin(progress * Math.PI);
        glowIntensity = 1 + 0.8 * Math.sin(progress * Math.PI);
        if (progress >= 1) {
          g.ballAnim = "idle";
          g.ballY = BALL_Y;
        }
      } else if (g.ballAnim === "fall") {
        const elapsed = now - g.animStartTime;
        const progress = elapsed / 600;
        ballDrawY = BALL_Y + progress * progress * 80;
        fallAlpha = Math.max(0, 1 - progress * 0.8);
        glowIntensity = 0.4;
        if (elapsed > 900) {
          g.ballAnim = "recover";
          g.ballY = BALL_Y - 60;
          g.animStartTime = now;
        }
      } else if (g.ballAnim === "recover") {
        const elapsed = now - g.animStartTime;
        const progress = Math.min(1, elapsed / 400);
        ballDrawY = BALL_Y - 60 + 60 * progress;
        fallAlpha = progress;
        if (progress >= 1) {
          g.ballAnim = "idle";
          g.ballY = BALL_Y;
        }
      } else {
        // Idle: small breathing bob
        ballDrawY = BALL_Y - 3 * Math.sin(now * 0.003);
      }

      // ── Draw everything ──────────────────────────────────────────────────
      ctx.clearRect(0, 0, CW, CH);
      drawBackground(ctx);
      drawHitZoneLine(ctx);

      // Draw tiles (back to front = far to near = high z to low z)
      const currentBeat = g.currentBeat;
      const activeTileIdx = Math.min(g.matchedCount, TOTAL_TILES - 1);

      // Draw all visible tiles
      for (let i = TOTAL_TILES - 1; i >= 0; i--) {
        const tile = TILES[i];
        const zFront = tile.beatStart - currentBeat;
        const zBack = tile.beatStart + tile.beats - currentBeat;
        if (zBack < -0.5 || zFront > VISIBLE_BEATS + 0.5) continue;
        const isActive = i === activeTileIdx;
        const col = NOTE_COLORS[tile.note] || NOTE_COLORS.C;
        drawTile(ctx, tile, currentBeat, showGuide);
        // Pulse active tile
        if (isActive && zFront > -0.2 && zFront < 1.5) {
          const pulseAlpha = 0.2 + 0.15 * Math.sin(now * 0.006);
          const front = project(Math.max(0, zFront));
          const back = project(Math.max(0.05, zBack));
          const cx = CW / 2;
          ctx.save();
          ctx.globalAlpha = pulseAlpha;
          ctx.beginPath();
          ctx.moveTo(cx - back.hw, back.y);
          ctx.lineTo(cx + back.hw, back.y);
          ctx.lineTo(cx + front.hw, front.y);
          ctx.lineTo(cx - front.hw, front.y);
          ctx.closePath();
          ctx.fillStyle = col.highlight;
          ctx.fill();
          ctx.restore();
        }
      }

      // Miss flash overlay
      if (g.missFlash > 0) {
        ctx.save();
        ctx.globalAlpha = g.missFlash * 0.35;
        ctx.fillStyle = "#ef4444";
        ctx.fillRect(0, 0, CW, CH);
        ctx.restore();
      }

      // Sparkles on hit
      if (g.hitSparkles > 0) {
        ctx.save();
        ctx.globalAlpha = g.hitSparkles / 8;
        drawSparkles(ctx, CW / 2, BALL_Y, 8);
        ctx.restore();
        ctx.globalAlpha = 1;
      }

      // Ball
      drawBall(ctx, CW / 2, ballDrawY, glowIntensity, fallAlpha);

      // Completion overlay
      if (isComplete) {
        ctx.save();
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = "#7c3aed";
        ctx.fillRect(0, 0, CW, CH);
        ctx.globalAlpha = 1;
        ctx.font = "bold 28px Arial";
        ctx.fillStyle = "#fde047";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("✨ Doron is sleeping! ✨", CW / 2, CH / 2);
        ctx.restore();
      }

      // Pre-start overlay
      if (!hasStarted) {
        ctx.save();
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = "#0a0320";
        ctx.fillRect(0, 0, CW, CH);
        ctx.globalAlpha = 1;
        ctx.font = "bold 17px Arial";
        ctx.fillStyle = "#c4b5fd";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("🎵 Press Play Now to start!", CW / 2, CH / 2 - 12);
        ctx.font = "13px Arial";
        ctx.fillStyle = "#a78bfa";
        ctx.fillText("Play the notes to keep Doron sleeping...", CW / 2, CH / 2 + 16);
        ctx.restore();
      }
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [hasStarted, isComplete, showGuide]);

  // ─── Doron image & classes ─────────────────────────────────────────────────
  const doronSrc =
    doronEmotion === "asleep"
      ? `/images/sleeping-doron.png`
      : `/images/DORON.png`;

  const doronStyle = useMemo(() => {
    const base = {
      transition: "all 0.5s ease",
      filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.4))",
    };
    if (doronEmotion === "fire") {
      return { ...base, filter: "drop-shadow(0 0 16px #ef4444) drop-shadow(0 4px 8px rgba(0,0,0,0.3))" };
    }
    if (doronEmotion === "asleep") {
      return { ...base, filter: "drop-shadow(0 0 12px #8b5cf6) drop-shadow(0 4px 8px rgba(0,0,0,0.3))", opacity: 0.85 };
    }
    return base;
  }, [doronEmotion]);

  // ─── zZZ sleepiness indicator ─────────────────────────────────────────────
  const sleepinessLevel = Math.floor((matchedCount / TOTAL_TILES) * 4); // 0..4

  return (
    <div className="w-full flex flex-col items-center gap-0 select-none">
      {/* ── Game Canvas ─────────────────────────────────────────────────── */}
      <div className="relative w-full max-w-[480px]">
        <canvas
          ref={canvasRef}
          width={CW}
          height={CH}
          className="w-full rounded-2xl sm:rounded-3xl border border-purple-900/40 shadow-xl"
          style={{ background: "#04021a" }}
        />

        {/* ── Training wheels toggle ──────────────────────────────────── */}
        <button
          onClick={() => setShowGuide((v) => !v)}
          className={
            "absolute top-2 right-2 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-bold " +
            "border transition-all shadow-sm " +
            (showGuide
              ? "bg-blue-600/90 border-blue-400 text-white"
              : "bg-black/50 border-white/20 text-white/60")
          }
          title={showGuide ? "Hide note guides (harder)" : "Show note guides (easier)"}
          style={{ touchAction: "manipulation" }}
        >
          🎯 Guide: {showGuide ? "ON" : "OFF"}
        </button>

        {/* ── Note colour legend ──────────────────────────────────────── */}
        {hasStarted && !isComplete && (
          <div className="absolute bottom-2 left-2 flex gap-1 flex-wrap max-w-[200px]">
            {Object.entries(NOTE_COLORS).map(([note, col]) => (
              <span
                key={note}
                className="px-1.5 py-0 rounded text-[9px] font-black"
                style={{ background: col.fill, color: col.text }}
              >
                {note}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Doron Area ──────────────────────────────────────────────────────── */}
      <div
        className="relative w-full max-w-[480px] flex items-end justify-between px-4"
        style={{
          background: "linear-gradient(to bottom, #1e0f52, #0a0320)",
          borderRadius: "0 0 20px 20px",
          minHeight: 110,
          marginTop: -4,
        }}
      >
        {/* zZZ indicators */}
        <div className="flex flex-col items-center gap-1 pt-3 pb-2 z-10">
          <div className="text-[10px] sm:text-xs font-bold text-purple-300">
            {doronEmotion === "fire"
              ? "🔥 MISS!"
              : doronEmotion === "asleep"
              ? "💤 Sleeping..."
              : doronEmotion === "sleepy"
              ? "😪 Getting sleepy..."
              : doronEmotion === "groggy"
              ? "😑 Getting drowsy..."
              : "😤 Wide awake!"}
          </div>

          {/* Sleep progress bar */}
          <div className="w-28 sm:w-36 h-2.5 rounded-full bg-purple-900/60 overflow-hidden border border-purple-700/40">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${(matchedCount / TOTAL_TILES) * 100}%`,
                background:
                  doronEmotion === "fire"
                    ? "#ef4444"
                    : "linear-gradient(to right, #8b5cf6, #c4b5fd)",
              }}
            />
          </div>

          {/* zZZ floating */}
          {sleepinessLevel >= 1 && (
            <div className="flex gap-1">
              {Array.from({ length: sleepinessLevel }).map((_, i) => (
                <span
                  key={i}
                  className="text-purple-300 font-black"
                  style={{
                    fontSize: 10 + i * 3,
                    opacity: 0.4 + i * 0.2,
                    animation: `bounce ${1 + i * 0.3}s ease-in-out infinite`,
                    animationDelay: `${i * 0.25}s`,
                  }}
                >
                  z
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Doron character */}
        <div
          className="relative z-10"
          style={{
            animation:
              doronEmotion === "fire"
                ? "none"
                : doronEmotion === "asleep"
                ? "none"
                : "none",
          }}
        >
          <img
            src={doronSrc}
            alt="Doron the Dragon"
            className="h-20 sm:h-24 w-auto object-contain"
            style={doronStyle}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            draggable={false}
          />

          {/* Fire effect */}
          {doronEmotion === "fire" && (
            <div
              className="absolute -top-6 left-1/2 -translate-x-1/2 text-2xl"
              style={{ animation: "pulse 0.3s ease-in-out infinite" }}
            >
              🔥
            </div>
          )}

          {/* Zzz floating above doron when asleep */}
          {doronEmotion === "asleep" && (
            <div
              className="absolute -top-8 right-0 text-purple-300 font-black text-lg"
              style={{ animation: "bounce 1.5s ease-in-out infinite" }}
            >
              💤
            </div>
          )}
        </div>

        {/* Ground platform glow */}
        <div
          className="absolute bottom-0 left-0 right-0 h-1 rounded-b-[20px]"
          style={{
            background:
              doronEmotion === "fire"
                ? "linear-gradient(to right, transparent, #ef4444, transparent)"
                : "linear-gradient(to right, transparent, #7c3aed, transparent)",
            opacity: 0.8,
          }}
        />
      </div>
    </div>
  );
}

/* ─── MAIN GAME LEVEL 1 PAGE ─────────────────────────────────────────────────── */
export default function GameLevel1() {
  const navigate = useNavigate();
  const [gameStarted, setGameStarted] = useState(false);
  const [showStarModal, setShowStarModal] = useState(false);

  return (
    <>
      <LevelTemplate
        levelId={1}
        // ── Song data for LevelTemplate's note-matching engine ──────────────
        melodyLines={MELODY_LINES}
        lyricGroupsPerLine={LYRIC_GROUPS}
        noteGroupIndexesPerLine={NOTE_GROUP_INDEXES}
        // ── Game hero: the 3D ball bouncer replaces the default hero ────────
        renderHero={({
          isComplete,
          noteEvent,
          matchedCount,
          totalNotes,
        }) => (
          <BallBouncerGame
            matchedCount={matchedCount}
            noteEvent={noteEvent}
            isComplete={isComplete}
            hasStarted={gameStarted}
            totalNotes={totalNotes}
          />
        )}
        // ── Callbacks ──────────────────────────────────────────────────────
        onStartGame={() => {
          TrophyStorage.startRound();
          setGameStarted(true);
        }}
        onComplete={() => {
          TrophyStorage.markLevelComplete(1);
          setShowStarModal(true);
        }}
        // ── Level identity & copy ──────────────────────────────────────────
        currentLevelForSidebar={1}
        startLabel="🎮 Start Game"
        completionTitle="Doron is snoring! 🐉💤"
        completionBody={
          <>
            You played all the notes perfectly — Doron fell fast asleep!{" "}
            <span className="font-semibold text-purple-700">Collect your star</span>{" "}
            and continue the Treble Town quest!
          </>
        }
        previewAudioSrc="/audio/previews/level-1-preview.mp3"
        backgroundImage="/images/Doron_Battle_Background.png"
        mobileMeterBelowPiano
        // ── Restrict piano to C4–C5 (beginner range for Twinkle Twinkle) ──
        firstKeyOverride="C4"
        lastKeyOverride="C5"
      />

      {/* ── Post-completion star modal ──────────────────────────────────── */}
      {showStarModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-[28px] shadow-2xl max-w-sm w-full p-6 flex flex-col items-center gap-4 text-center">
            <div className="text-5xl">⭐</div>
            <h2 className="text-xl font-black text-slate-900">
              Amazing — Doron is asleep!
            </h2>
            <p className="text-sm text-slate-600">
              You put Doron to sleep with Twinkle Twinkle! The Treble Town quest
              has begun. Play all 10 songs to defeat him for good!
            </p>
            <img
              src="/images/sleeping-doron.png"
              alt="Sleeping Doron"
              className="h-20 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="flex gap-3 w-full">
              <button
                type="button"
                className="flex-1 px-4 py-3 rounded-full bg-purple-600 text-white font-extrabold shadow-lg hover:bg-purple-700 transition"
                onClick={() => {
                  setShowStarModal(false);
                  navigate("/level-2");
                }}
              >
                Next Level →
              </button>
              <button
                type="button"
                className="px-4 py-3 rounded-full border-2 border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition"
                onClick={() => setShowStarModal(false)}
              >
                Stay
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}